'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { loadQueue, getPendingOperations, markOperationSynced, markOperationFailed, markOperationConflict, getAllUserOperations, type QueuedOperation, type ConnectionStatus, type ConflictInfo, detectConflict,  } from '@/lib/offlineQueue';
import { createClient } from '@/lib/supabase/client';

export interface SyncState {
  connectionStatus: ConnectionStatus;
  pendingCount: number;
  syncingCount: number;
  failedCount: number;
  conflictCount: number;
  lastSyncedAt: string | null;
  conflicts: ConflictInfo[];
}

interface UseOfflineSyncOptions {
  userId: string | null;
  onSyncComplete?: (synced: number) => void;
  onConflict?: (conflict: ConflictInfo) => void;
}

export function useOfflineSync({ userId, onSyncComplete, onConflict }: UseOfflineSyncOptions): SyncState & {
  dismissConflict: (operationId: string) => void;
  retryFailed: () => void;
} {
  const [isOnline, setIsOnline] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncState, setSyncState] = useState<SyncState>({
    connectionStatus: 'online',
    pendingCount: 0,
    syncingCount: 0,
    failedCount: 0,
    conflictCount: 0,
    lastSyncedAt: null,
    conflicts: [],
  });
  const syncInProgressRef = useRef(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Recompute counts from queue
  const refreshCounts = useCallback(() => {
    if (!userId) return;
    const all = getAllUserOperations(userId);
    const pending = all.filter((q) => q.status === 'pending').length;
    const syncing = all.filter((q) => q.status === 'syncing').length;
    const failed = all.filter((q) => q.status === 'failed').length;
    const conflicts = all.filter((q) => q.status === 'conflict');

    setSyncState((prev) => ({
      ...prev,
      pendingCount: pending,
      syncingCount: syncing,
      failedCount: failed,
      conflictCount: conflicts.length,
      conflicts: conflicts.map((c) => ({
        operationId: c.operationId,
        recordId: c.recordId,
        table: c.table,
        module: c.module,
        localUpdatedAt: c.updatedAt,
        remoteUpdatedAt: c.lastError || '',
        localData: c.data,
        remoteData: null,
      })),
    }));
  }, [userId]);

  // Process the offline queue
  const processQueue = useCallback(async () => {
    if (!userId || !isOnline || syncInProgressRef.current) return;
    const pending = getPendingOperations(userId);
    if (pending.length === 0) {
      refreshCounts();
      return;
    }

    syncInProgressRef.current = true;
    setIsSyncing(true);
    setSyncState((prev) => ({ ...prev, connectionStatus: 'syncing' }));

    const supabase = createClient();
    let syncedCount = 0;

    for (const op of pending) {
      try {
        // Check for conflict on update operations
        if (op.type === 'update') {
          const { data: remoteRecord } = await supabase
            .from(op.table)
            .select('*')
            .eq('id', op.recordId)
            .maybeSingle();

          if (remoteRecord) {
            const conflict = detectConflict(op, remoteRecord);
            if (conflict) {
              markOperationConflict(op.operationId, remoteRecord.updated_at || remoteRecord.updatedAt || '');
              onConflict?.(conflict);
              continue;
            }
          }
        }

        // Execute the operation
        let error: any = null;

        if (op.type === 'create') {
          const { error: e } = await supabase.from(op.table).upsert(op.data, { onConflict: 'id' });
          error = e;
        } else if (op.type === 'update') {
          const { error: e } = await supabase.from(op.table).upsert(op.data, { onConflict: 'id' });
          error = e;
        } else if (op.type === 'delete') {
          const { error: e } = await supabase.from(op.table).delete().eq('id', op.recordId);
          error = e;
        }

        if (error) {
          markOperationFailed(op.operationId, error.message || 'Unknown error');
        } else {
          markOperationSynced(op.operationId);
          syncedCount++;
        }
      } catch (err: any) {
        markOperationFailed(op.operationId, err?.message || 'Network error');
      }
    }

    syncInProgressRef.current = false;
    setIsSyncing(false);
    refreshCounts();

    if (syncedCount > 0) {
      setSyncState((prev) => ({
        ...prev,
        connectionStatus: 'online',
        lastSyncedAt: new Date().toISOString(),
      }));
      onSyncComplete?.(syncedCount);
    } else {
      setSyncState((prev) => ({ ...prev, connectionStatus: 'online' }));
    }
  }, [userId, isOnline, refreshCounts, onConflict, onSyncComplete]);

  // Online/offline detection
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => {
      setIsOnline(true);
      setSyncState((prev) => ({ ...prev, connectionStatus: 'online' }));
    };
    const handleOffline = () => {
      setIsOnline(false);
      setSyncState((prev) => ({ ...prev, connectionStatus: 'offline' }));
    };

    setIsOnline(navigator.onLine);
    if (!navigator.onLine) {
      setSyncState((prev) => ({ ...prev, connectionStatus: 'offline' }));
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Process queue when coming online
  useEffect(() => {
    if (isOnline && userId) {
      // Small delay to let network stabilize
      const timer = setTimeout(() => processQueue(), 1500);
      return () => clearTimeout(timer);
    }
  }, [isOnline, userId, processQueue]);

  // Periodic retry for failed operations (every 30s when online)
  useEffect(() => {
    if (!userId || !isOnline) return;
    const interval = setInterval(() => {
      refreshCounts();
      processQueue();
    }, 30_000);
    return () => clearInterval(interval);
  }, [userId, isOnline, processQueue, refreshCounts]);

  // Initial count load
  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  // Update connection status based on isSyncing
  useEffect(() => {
    if (!isOnline) {
      setSyncState((prev) => ({ ...prev, connectionStatus: 'offline' }));
    } else if (isSyncing) {
      setSyncState((prev) => ({ ...prev, connectionStatus: 'syncing' }));
    } else {
      setSyncState((prev) => ({ ...prev, connectionStatus: 'online' }));
    }
  }, [isOnline, isSyncing]);

  const dismissConflict = useCallback((operationId: string) => {
    // Remove conflict from queue (user chose to discard local change)
    const queue = loadQueue().filter((q) => q.operationId !== operationId);
    if (typeof window !== 'undefined') {
      localStorage.setItem('ega_offline_queue', JSON.stringify(queue));
    }
    refreshCounts();
  }, [refreshCounts]);

  const retryFailed = useCallback(() => {
    if (!userId) return;
    const queue = loadQueue().map((q) => {
      if (q.userId === userId && q.status === 'failed') {
        return { ...q, status: 'pending' as const, nextRetryAt: undefined };
      }
      return q;
    });
    if (typeof window !== 'undefined') {
      localStorage.setItem('ega_offline_queue', JSON.stringify(queue));
    }
    refreshCounts();
    processQueue();
  }, [userId, refreshCounts, processQueue]);

  return { ...syncState, dismissConflict, retryFailed };
}
